import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { TextField } from "@mui/material";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { LoadingState, StatTile } from "../../components/UI/States";
import {
  BackLink,
  BranchSelect,
  InspectionDialog,
  InspectionTable,
  Notice,
  SearchField,
  Segmented,
} from "../../components/Fleet/FleetParts";
import {
  HISTORY_DAYS,
  downloadText,
  durationSummary,
  formatDuration,
  inspectionCsvColumns,
  isFlagged,
  localDayKey,
  startOfLocalDay,
  toCsv,
} from "../../components/Fleet/fleetFormat";
import { fetchInspections } from "./fleetApi";

// Every forklift check-in (mobile app + branch kiosk) and vehicle pre-trip
// inspection from the last 90 days in one searchable list. The whole window
// is fetched once (a few thousand small rows) and filtered here, so every
// filter change is instant. Filters live in the URL so they can be linked:
//   ?type=forklift|vehicle &range=today|7|30|90 &from=&to= (YYYY-MM-DD)
//   &branch= &result=flagged|pass|unsafe|out &q=

const RANGES = [
  { value: "today", label: "Today", days: 1 },
  { value: "7", label: "7 days", days: 7 },
  { value: "30", label: "30 days", days: 30 },
  { value: "90", label: `${HISTORY_DAYS} days`, days: HISTORY_DAYS },
];

const RESULTS = {
  all: { label: "All results", test: () => true },
  flagged: { label: "Flagged", test: isFlagged },
  pass: { label: "Passed", test: (e) => e.result === "pass" },
  unsafe: { label: "Not safe", test: (e) => e.result === "unsafe" },
  out: { label: "Not in service", test: (e) => e.result === "out" },
  missed: { label: "Drove, no inspection", test: (e) => e.result === "missed" },
  oos: { label: "Out of service", test: (e) => e.result === "oos" || e.result === "back" },
};


const InspectionLog = () => {
  const user = useSelector(selectUser);
  const [params, setParams] = useSearchParams();
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const type = ["forklift", "vehicle"].includes(params.get("type")) ? params.get("type") : "all";
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const range = from || to ? "custom" : RANGES.some((r) => r.value === params.get("range")) ? params.get("range") : "7";
  const branch = params.get("branch") || "";
  const result = RESULTS[params.get("result")] ? params.get("result") : "all";
  const query = params.get("q") || "";

  const setParam = (updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "all") next.set(key, value);
      else next.delete(key);
    });
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await fetchInspections(user.id);
      setEntries(data.entries);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const oldestDay = localDayKey(startOfLocalDay(HISTORY_DAYS - 1));
  const today = localDayKey(new Date());

  // Date window as local day keys (inclusive).
  const [startDay, endDay] = useMemo(() => {
    if (range === "custom") return [from || oldestDay, to || today];
    const days = RANGES.find((r) => r.value === range)?.days || 7;
    return [localDayKey(startOfLocalDay(days - 1)), today];
  }, [range, from, to, oldestDay, today]);

  const inWindow = useMemo(
    () =>
      (entries || []).filter((e) => {
        const day = localDayKey(e.at);
        return day >= startDay && day <= endDay;
      }),
    [entries, startDay, endDay]
  );

  const branches = useMemo(
    () => [...new Set((entries || []).map((e) => e.branch).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [entries]
  );
  const selectedBranch = branches.find((b) => b.toLowerCase() === branch.toLowerCase()) || "";

  // Everything except the result filter — the result chips count from this.
  const scoped = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inWindow
      .filter((e) => type === "all" || e.type === type)
      .filter((e) => !branch || e.branch.toLowerCase() === branch.toLowerCase())
      .filter(
        (e) =>
          !q ||
          [
            e.asset,
            `#${e.asset}`,
            // A renamed truck's entries are also found by the label they were recorded under.
            e.recordedAsset,
            e.inspector,
            e.branch,
            e.shiftName,
            e.notes,
            e.entryID,
            ...e.flagged.flatMap((f) => [f.label, f.note]),
          ]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(q))
      );
  }, [inWindow, type, branch, query]);

  const visible = useMemo(() => scoped.filter(RESULTS[result].test), [scoped, result]);

  const stats = useMemo(
    () => ({
      forklift: scoped.filter((e) => e.type === "forklift" && e.source !== "status").length,
      vehicle: scoped.filter((e) => e.type === "vehicle" && e.source === "mobile").length,
      autoDays: scoped.filter((e) => e.source === "gps").length,
      flagged: scoped.filter(isFlagged).length,
      time: durationSummary(scoped.filter((e) => e.source === "mobile" || e.source === "kiosk")),
      inspectors: new Set(
        scoped
          .filter((e) => e.source === "mobile" || e.source === "kiosk")
          .map((e) => e.inspector.toLowerCase())
          .filter(Boolean)
      ).size,
    }),
    [scoped]
  );

  // The list holds summaries; the export re-fetches the same window with
  // every checklist answer and keeps exactly the rows on screen, in order.
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { entries: detailed } = await fetchInspections(user.id, {
        type: type === "all" ? "" : type,
        branch,
        from: new Date(`${startDay}T00:00:00`).toISOString(),
        to: new Date(`${endDay}T23:59:59.999`).toISOString(),
        detail: true,
      });
      const byKey = new Map(detailed.map((e) => [e.key, e]));
      const rows = visible.map((e) => byKey.get(e.key) || e);
      downloadText(`fleet-inspections_${startDay}_to_${endDay}.csv`, toCsv(rows, inspectionCsvColumns(rows)));
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell size="2xl">
      <InspectionDialog entry={open} onClose={() => setOpen(null)} userId={user?.id} showAssetLink />

      <BackLink to="/fleet">Fleet</BackLink>
      <PageHeader
        icon={FactCheckRoundedIcon}
        tone="teal"
        eyebrow="Fleet"
        title="Inspection Log"
        description={`Every forklift check-in and vehicle pre-trip inspection from the last ${HISTORY_DAYS} days — search by unit, inspector, branch, shift, or flagged item. Trucks with no inspection get an automatic entry from their GPS odometer.`}
        actions={
          <>
            <button type="button" className="app-btn app-btn-secondary" onClick={load} disabled={loading}>
              <RefreshRoundedIcon sx={{ fontSize: 18 }} /> Refresh
            </button>
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={exportCsv}
              disabled={!visible.length || exporting}
              title="Download the rows shown, with every checklist answer"
            >
              <DownloadRoundedIcon sx={{ fontSize: 18 }} /> {exporting ? "Preparing…" : "Export CSV"}
            </button>
          </>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!entries && !error && <LoadingState label={`Loading ${HISTORY_DAYS} days of inspections…`} />}

      {entries && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatTile
              label="Forklift check-ins"
              value={stats.forklift.toLocaleString()}
              active={type === "forklift"}
              onClick={() => setParam({ type: type === "forklift" ? "all" : "forklift" })}
            />
            <StatTile
              label="Vehicle inspections"
              value={stats.vehicle.toLocaleString()}
              hint={stats.autoDays ? `+${stats.autoDays.toLocaleString()} no-inspection days (GPS)` : undefined}
              active={type === "vehicle"}
              onClick={() => setParam({ type: type === "vehicle" ? "all" : "vehicle" })}
            />
            <StatTile
              label="Flagged"
              value={stats.flagged.toLocaleString()}
              hint="Needs attention or not safe"
              tone={stats.flagged ? "amber" : "default"}
              active={result === "flagged"}
              onClick={() => setParam({ result: result === "flagged" ? "all" : "flagged" })}
            />
            <StatTile label="Inspectors" value={stats.inspectors} hint="Different people" />
            <StatTile
              label="Typical inspection time"
              value={stats.time.median !== null ? formatDuration(stats.time.median) : "—"}
              hint={stats.time.timed ? `${stats.time.quick.toLocaleString()} of ${stats.time.timed.toLocaleString()} under 30 seconds` : "Not timed"}
              tone={stats.time.quick ? "amber" : "default"}
            />
          </div>

          <div className="app-card mb-4 space-y-3 rounded-2xl border p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <SearchField
                value={query}
                onChange={(value) => setParam({ q: value })}
                placeholder="Search unit, inspector, item, notes…"
                className="w-full lg:w-80"
              />
              <BranchSelect value={selectedBranch} onChange={(v) => setParam({ branch: v })} branches={branches} />
              <Segmented
                label="Type"
                value={type}
                onChange={(v) => setParam({ type: v })}
                options={[
                  { value: "all", label: "All" },
                  { value: "forklift", label: "Forklifts" },
                  { value: "vehicle", label: "Vehicles" },
                ]}
              />
            </div>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Segmented
                  label="Date range"
                  value={range}
                  onChange={(v) => v !== "custom" && setParam({ range: v === "7" ? "" : v, from: "", to: "" })}
                  options={[...RANGES, ...(range === "custom" ? [{ value: "custom", label: "Custom" }] : [])]}
                />
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={range === "custom" ? from : startDay}
                  onChange={(e) => setParam({ from: e.target.value, to: range === "custom" ? to : endDay, range: "" })}
                  inputProps={{ min: oldestDay, max: today }}
                  InputLabelProps={{ shrink: true }}
                  className="w-40"
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={range === "custom" ? to : endDay}
                  onChange={(e) => setParam({ to: e.target.value, from: range === "custom" ? from : startDay, range: "" })}
                  inputProps={{ min: oldestDay, max: today }}
                  InputLabelProps={{ shrink: true }}
                  className="w-40"
                />
              </div>
              <Segmented
                label="Result"
                value={result}
                onChange={(v) => setParam({ result: v })}
                options={Object.entries(RESULTS).map(([value, r]) => ({
                  value,
                  label: r.label,
                  count: scoped.filter(r.test).length,
                }))}
              />
            </div>
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <p className="text-sm font-semibold tabular-nums">
                {visible.length.toLocaleString()} inspection{visible.length === 1 ? "" : "s"}
              </p>
              <p className="app-muted text-xs">History goes back {HISTORY_DAYS} days.</p>
            </div>
            <div className="border-t app-divider">
              <InspectionTable
                entries={visible}
                onOpen={setOpen}
                show={{ asset: true, branch: true, source: true }}
                emptyTitle="No inspections match these filters."
              />
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default InspectionLog;
